import { currencyFormatter, noDecimalNumberFormatter } from "./data-formatters";

/**
 * Calculates the estimated monthly impact for a currency-based metric.
 * Extrapolates from per-visitor values and test duration to a 30-day period.
 */
export function getEstImpactCurrencyMetric(
  perVisA: number,
  perVisB: number,
  totalTestVisitors: number,
  daysInTest: number
): number {
  return Math.round(
    (perVisB - perVisA) * totalTestVisitors * (30 / daysInTest)
  );
}

/**
 * Calculates a formatted estimated monthly impact string.
 * Adapted from frontend calculateEstImpactTb — no DashboardPageName dependency.
 *
 * @param dataTypes - The data types from the graph config (e.g. ["currency"], ["percentTwoDecimal"])
 * @param visitors - Total test visitors across all variations
 * @param baseValue - Control per-visitor metric value
 * @param compareValue - Treatment per-visitor metric value
 * @param storeCurrency - Store currency config for formatting
 * @param testDuration - Number of days the test has been running
 * @param metricSuffix - Override suffix (e.g. "ORDERS", "UNITS"). Auto-detected from title if not provided.
 * @param title - Optional metric title used for auto-detecting suffix
 */
export function calculateEstImpact(
  dataTypes: string[],
  visitors: number,
  baseValue: number,
  compareValue: number,
  storeCurrency: { code: string; locale: string },
  testDuration: number,
  metricSuffix?: string,
  title?: string
): string {
  const estValue = getEstImpactCurrencyMetric(
    baseValue,
    compareValue,
    visitors,
    testDuration
  );

  if (dataTypes[0]?.includes("currency")) {
    return `${estValue > 0 ? "+" : ""}${currencyFormatter(
      estValue,
      false,
      storeCurrency
    )} MONTHLY`;
  }

  const suffix =
    metricSuffix ??
    (title?.includes("Unit")
      ? "UNITS"
      : title?.includes("View")
        ? "VIEWS"
        : "ORDERS");

  return `${estValue > 0 ? "+" : ""}${noDecimalNumberFormatter(
    estValue,
    false,
    storeCurrency
  )} ${suffix} MONTHLY`;
}

/**
 * Returns true when the lift percentage is effectively zero (rounds to 0%).
 * Matches the frontend "No Change" logic: parseInt(formatted) === 0,
 * which triggers for any |liftPct| < 1.
 *
 * @param liftPct - Lift as a percentage (e.g. 0.4 means 0.4%)
 */
export function isNoChangeLift(liftPct: number): boolean {
  return Math.floor(Math.abs(liftPct)) === 0;
}

/**
 * Formats an uplift CI bound as a percentage string.
 * E.g. 0.05 → "5%", -0.023 → "-2%"
 */
export function formatUpliftCI(
  value: number,
  locale?: string,
  options?: Intl.NumberFormatOptions
): string {
  const valuePct = value * 100;
  if (isNoChangeLift(valuePct)) {
    return `0%`;
  }
  return `${valuePct > 0 ? "+" : ""}${new Intl.NumberFormat(locale || "en-US", {
    style: "decimal",
    maximumFractionDigits: 0,
    ...options,
  }).format(valuePct)}%`;
}
